import { BrowserModule } from '@angular/platform-browser';
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  LOCALE_ID,
  NgModule,
} from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LayoutModule } from '@angular/cdk/layout';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatPaginatorModule } from '@angular/material/paginator';

import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { environment } from '../environments/environment';
import { PrivateComponent } from './private/private.component';
import { PublicComponent } from './public/public.component';
import { ReportContainerComponent } from './report-container/report-container.component';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ReportDirective } from './report-container/report.directive';
import { ReportModuleLoaderComponent } from './report-module-loader/report-module-loader.component';
import { ReportModulesService } from './services/report-modules/report-modules.service';
import proj4 from 'proj4';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { MatTreeModule } from '@angular/material/tree';
import { MatTabsModule } from '@angular/material/tabs';
import { MatRadioModule } from '@angular/material/radio';
import { MatSliderModule } from '@angular/material/slider';

import {
  DateAdapter,
  MatNativeDateModule,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DatePipe, DecimalPipe, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeIt from '@angular/common/locales/it';
import {
  MomentDateAdapter,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
} from '@angular/material-moment-adapter';

import { ImageViewerComponent } from './modules/image-viewer/component/image-viewer.component';
import { MarkdownModuleComponent } from './modules/markdown-module/component/markdown-module.component';
import { LMarkdownEditorModule } from 'ngx-markdown-editor';
import { NgChartsModule } from 'ng2-charts';
import { SideNavbarComponent } from './side-navbar/side-navbar.component';
import { SettingsDialogComponent } from './dialogs/settings-dialog/settings-dialog.component';
import { ProjectDialogComponent } from './dialogs/project-dialog/project-dialog.component';
import { ReportDialogComponent } from './dialogs/report-dialog/report-dialog.component';
import { ClientDialogComponent } from './dialogs/client-dialog/client-dialog.component';
import {
  MatDialogModule,
  MAT_DIALOG_DEFAULT_OPTIONS,
} from '@angular/material/dialog';
import { AddSelectDialogComponent } from './dialogs/add-select-dialog/add-select-dialog.component';
import { BaseDialogComponent } from './dialogs/base-dialog/base-dialog.component';
import { ImageViewerConfigComponent } from './modules/image-viewer/config/image-viewer-config/image-viewer-config.component';
import { MarkdownModuleConfigComponent } from './modules/markdown-module/config/markdown-module-config/markdown-module-config.component';
import { ConfirmationDialogComponent } from './confirmation-dialog/confirmation-dialog.component';
import { DatasourceConfigComponent } from './datasource/datasource-config/datasource-config.component';
import { DatasourceConfigDialogComponent } from './datasource/datasource-config-dialog/datasource-config-dialog.component';

import { register } from 'ol/proj/proj4';
import { SettingsPageComponent } from './settings-page/settings-page.component';
import { UserConfigComponent } from './user/user-config/user-config.component';
import { UserConfigDialogComponent } from './user/user-config-dialog/user-config-dialog.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { GlobalHttpInterceptor } from './error-handler/global-http-interceptor.service';
import { NgApexchartsModule } from 'ng-apexcharts';

import * as moment from 'moment';
import { DisplayInfoImageComponent } from './dialogs/display-image/display-info-image.component';
import { ProjectsClientsConfigComponent } from './settings_components/projects-clients-config/projects-clients-config.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatSortModule } from '@angular/material/sort';
import { ClientsConfigComponent } from './settings_components/clients-config/clients-config.component';
import { WebcamComponent } from './modules/webcam/component/webcam.component';
import { WebcamConfigComponent } from './modules/webcam/config/webcam-config/webcam-config.component';
import { GenericCorrelationComponent } from './modules/correlation_generic/component/correlation.component';
import { GenericCorrelationConfigComponent } from './modules/correlation_generic/config/correlation-config/correlation-config.component';
import { NgxSpinnerModule } from 'ngx-spinner';
import { GenericOrthofotoComponent } from './modules/orthofoto_generic/component/orthofoto.component';
import { GenericOrthofotoConfigComponent } from './modules/orthofoto_generic/config/orthofoto-config.component';
import { GenericMovementScatterplotComponent } from './modules/movement_scatterplot_generic/component/movement_scatterplot.component';
import { GenericMovementScatterplotConfigComponent } from './modules/movement_scatterplot_generic/config/movement_scatterplot-config';
import { MinMaxOrdinateFilter } from './user/tools';
import { GenericRoleBatteryComponent } from './modules/role-battery_generic/component/role-battery.component';
import { GenericRoleBatteryConfigComponent } from './modules/role-battery_generic/config/role-battery-config';
import { CorrelationLegendComponent } from './modules/correlation_generic/correlation-legend/correlation-legend.component';
import { DefaultDateRangeSelectorComponent } from './modules/correlation_generic/config/default-date-range-selector';
import { DefaultSmoothingSelectorComponent } from './modules/correlation_generic/config/default-smoothing-selector';
import { AccessLogsComponent } from './settings_components/access-logs/access-logs.component';
import { SensorLocalizationComponent } from './modules/sensor_localization/component/sensor-localization.component';
import { SensorLocalizationConfigComponent } from './modules/sensor_localization/config/sensor-localization-config.component';
import { SensorDetailsDialogComponent } from './modules/sensor_localization/details-dialog/sensor-details-dialog.component';
import {
  TranslateModule,
  TranslateLoader,
  TranslateService,
} from '@ngx-translate/core';
import {
  TranslateHttpLoader,
  TRANSLATE_HTTP_LOADER_CONFIG,
} from '@ngx-translate/http-loader';
import { LanguageService } from './services/language.service';
import { TranslateFieldPipe } from './pipes/translate-field.pipe';
import { CetDatePipe } from './pipes/cet-date.pipe';
import { GlobalTimeSettingsComponent } from './components/global-time-settings/global-time-settings.component';
import { ApiModule, Configuration } from './api/generated';
import { createKeycloakOptions } from './auth/keycloak-options';

function apiConfigurationFactory(): Configuration {
  return new Configuration({ basePath: environment.apiEndpoint });
}

function initializeLanguage(
  languageService: LanguageService,
  translateService: TranslateService
) {
  return () => {
    // Set default language
    translateService.setDefaultLang('de-AT');
    // Initialize and set language based on browser or stored preference
    // This will also call translateService.use() internally
    languageService.initializeLanguage();
    return Promise.resolve();
  };
}

moment.locale('de-AT');

// See the Moment.js docs for the meaning of these formats:
// https://momentjs.com/docs/#/displaying/format/
export const MY_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export interface ModuleConfiguration {
  displayName: string;
  settingsComponent: any;
  previewImagePath: string;
}

function initializeKeycloak(keycloak: KeycloakService) {
  return () =>
    keycloak.init(
      createKeycloakOptions(
        environment.keycloak,
        environment.apiEndpoint,
        window.location.origin
      )
    );
}

function initializeReportModules(reportModuleService: ReportModulesService) {
  return () => {
    reportModuleService.registerModule(
      GenericOrthofotoComponent,
      'orthophoto-generic'
    );
    reportModuleService.registerModule(
      GenericCorrelationComponent,
      'correlation-generic'
    );
    reportModuleService.registerModule(
      GenericMovementScatterplotComponent,
      'movement_scatterplot_generic'
    );
    reportModuleService.registerModule(WebcamComponent, 'webcam');

    reportModuleService.registerModule(
      GenericRoleBatteryComponent,
      'role-battery-generic'
    );
    reportModuleService.registerModule(ImageViewerComponent, 'image-viewer');
    reportModuleService.registerModule(MarkdownModuleComponent, 'markdown');
    reportModuleService.registerModule(
      SensorLocalizationComponent,
      'sensor-localization'
    );
  };
}

registerLocaleData(localeDe, 'de');
registerLocaleData(localeIt, 'it');

// Register projections
proj4.defs(
  'EPSG:3857',
  '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs'
);

// West
// Source: https://epsg.io/31254
proj4.defs(
  'EPSG:31254',
  '+proj=tmerc +lat_0=0 +lon_0=10.33333333333333 +k=1 +x_0=0 +y_0=-5000000 +ellps=bessel ' +
    '+towgs84=577.326,90.129,463.919,5.137,1.474,5.297,2.4232 +units=m +no_defs'
);

// Center
proj4.defs(
  'EPSG:31255',
  '+proj=tmerc +lat_0=0 +lon_0=13.33333333333333 +k=1 +x_0=0 +y_0=-5000000 +ellps=bessel ' +
    '+towgs84=577.326,90.129,463.919,5.137,1.474,5.297,2.4232 +units=m +no_defs'
);

// East
proj4.defs(
  'EPSG:31256',
  '+proj=tmerc +lat_0=0 +lon_0=16.33333333333333 +k=1 +x_0=0 +y_0=-5000000 +ellps=bessel ' +
    '+towgs84=577.326,90.129,463.919,5.137,1.474,5.297,2.4232 +units=m +no_defs'
);

register(proj4);

@NgModule({
  declarations: [
    AppComponent,
    PrivateComponent,
    PublicComponent,
    ReportContainerComponent,
    ReportDirective,
    ReportModuleLoaderComponent,
    GenericRoleBatteryComponent,
    ImageViewerComponent,
    MarkdownModuleComponent,
    SideNavbarComponent,
    SettingsDialogComponent,
    ProjectDialogComponent,
    ReportDialogComponent,
    ClientDialogComponent,
    AddSelectDialogComponent,
    BaseDialogComponent,
    GenericRoleBatteryConfigComponent,
    ImageViewerConfigComponent,
    MarkdownModuleConfigComponent,
    ConfirmationDialogComponent,
    DatasourceConfigComponent,
    DatasourceConfigDialogComponent,
    SettingsPageComponent,
    UserConfigComponent,
    UserConfigDialogComponent,
    NotFoundComponent,
    DisplayInfoImageComponent,
    ProjectsClientsConfigComponent,
    ClientsConfigComponent,
    WebcamComponent,
    WebcamConfigComponent,
    GenericCorrelationComponent,
    GenericCorrelationConfigComponent,
    GenericOrthofotoComponent,
    GenericOrthofotoConfigComponent,
    GenericMovementScatterplotComponent,
    GenericMovementScatterplotConfigComponent,
    MinMaxOrdinateFilter,
    CorrelationLegendComponent,
    DefaultDateRangeSelectorComponent,
    DefaultSmoothingSelectorComponent,
    AccessLogsComponent,
    SensorLocalizationComponent,
    SensorLocalizationConfigComponent,
    SensorDetailsDialogComponent,
    TranslateFieldPipe,
    CetDatePipe,
    GlobalTimeSettingsComponent,
  ],
  imports: [
    MatInputModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    LayoutModule,
    MatToolbarModule,
    MatButtonModule,
    MatSidenavModule,
    MatIconModule,
    MatListModule,
    MatGridListModule,
    MatCardModule,
    MatMenuModule,
    MatSnackBarModule,
    MatTreeModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatTabsModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatExpansionModule,
    KeycloakAngularModule,
    ApiModule.forRoot(apiConfigurationFactory),
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatRadioModule,
    MatSliderModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatSelectModule,
    LMarkdownEditorModule,
    NgApexchartsModule,
    NgChartsModule,
    DragDropModule,
    MatProgressSpinnerModule,
    NgxSpinnerModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: TranslateHttpLoader,
      },
      defaultLanguage: 'de-AT',
    }),
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeReportModules,
      multi: true,
      deps: [ReportModulesService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLanguage,
      multi: true,
      deps: [LanguageService, TranslateService],
    },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: () => {
        const stored = localStorage.getItem('app-language');
        return stored === 'it' ? 'it' : 'de-AT';
      },
    },
    {
      provide: LOCALE_ID,
      useFactory: () => {
        const stored = localStorage.getItem('app-language');
        return stored === 'it' ? 'it' : 'de-AT';
      },
    },
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: {
        prefix: './assets/i18n/',
        suffix: '.json',
      },
    },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },

    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { hasBackdrop: true } },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GlobalHttpInterceptor,
      multi: true,
    },

    DatePipe,
    DecimalPipe,
    provideHttpClient(withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
